import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { IsfModel } from '@app/data-layer/models/isf';
import { EntitiesRepository, ShipmentsV2Repository } from '@app/data-layer/repositories';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { omit } from 'lodash-es';
import { of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-supplier-details',
  templateUrl: './isf-ocr-quick-fix-supplier-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-supplier-details.component.scss'],
})
export class IsfOcrQuickFixSupplierDetailsComponent {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public supplierFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public supplierDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;

  public importer: EntityModel;
  public entities: null | any[] = null;
  public companyName: AbstractControl;
  public companyLoading: boolean = false;
  private valueSellerCompany: null | string = null;
  public CUSTOM_MASK: any = CUSTOM_MASK;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfStateService: IsfStateService,
    private entitiesRepository: EntitiesRepository,
    private companiesService: CompaniesService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.supplierFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    if (isNeedCheck && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.fh.markAsTouched(this.formGroup);
        this.openMissingFieldsModal(invalidFields);
        return;
      }
    }

    this.updateOrCreateSupplier()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.updateCurrentIsf(isf);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public changeAddress(item: any) {
    const valuesToPatch = omit(item, ['identifier', 'identifierQualifier']) as EntityModel;
    this.formGroup.patchValue(valuesToPatch);
    this.companyName.setValue(valuesToPatch.name);
    this.formGroup.get('entityIdentifier').setValue(item.identifier);
    this.formGroup.get('entityIdentifierQualifier').setValue(item.identifierQualifier);
  }

  public get filteredSupplierFields(): ISectionFormField[] {
    const fieldsToIgnore = ['name'];
    return this.supplierFields.filter((field) => !fieldsToIgnore.includes(field.name));
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const supplier = this.isf?.shipment?.seller;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const supplierAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(supplier?.address);
    const nameField = this.supplierFields.find((field) => field.name === 'name');

    this.supplierDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: supplier?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: supplierAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const supplierAddressErrors = this.isf?.shipment?.seller?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(supplierAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateSupplier() {
    const supplier: EntityModel = this.formGroup.value;
    const supplierId = this.formGroup.get('id').value;

    let newShipmentData: Partial<ShipmentModel>;

    if (supplierId === this.importer?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        seller: this.importer,
        sellerId: this.importer?.id,
      };
    } else {
      newShipmentData = {
        ...this.isf.shipment,
        seller: supplier,
        sellerId: supplierId,
      };
    }

    return of(supplier).pipe(
      switchMap((entity) => {
        if (!supplierId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.SELLER;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        return this.updateShipment({
          ...newShipmentData,
          seller: entity,
          sellerId: entity.id,
        });
      }),
      switchMap((newShipment) => this.isfStateService.updateShipment(newShipment)),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private updateShipment(newShipmentData?: Partial<ShipmentModel>) {
    if (!newShipmentData) {
      newShipmentData = { ...this.isf?.shipment };
    }

    return this.shipmentsV2Repository.update(this.isf?.shipment?.id, newShipmentData);
  }

  private initializeFormGroup() {
    const supplier = {
      ...this.isf?.shipment?.seller,
      entityCode: enumEntityModelCode.SELLER,
    };

    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.SELLER);

    this.formGroup.patchValue(this.isf?.shipment?.seller);

    this.companyName = this.formGroup.get('name');

    this.companyName.valueChanges
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([pre, cur]) => {
          if (!pre || !cur) {
            return false;
          }
          return Math.abs(cur.length - (pre ? pre.length : 0)) === 1;
        }),
        debounceTime(600),
        distinctUntilChanged(),
        filter(([pre, cur]) => {
          return cur && cur.length >= 3;
        }),
        switchMap((value) => {
          if (this.valueSellerCompany && value[1].indexOf(value[0]) === 0 && this.valueSellerCompany === value[0]) {
            return of(supplier);
          }
          this.companyLoading = true;
          const companyId: number = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;
          if (!companyId) {
            return of([]);
          }
          return this.entitiesRepository.searchByCompany(companyId, {
            name: value[1],
            type: supplier?.entityCode,
          });
        }),
        map((value) => value),
        tap((values: any[]) => {
          if (values?.length === 0) {
            this.valueSellerCompany = this.companyName.value;
          } else {
            this.valueSellerCompany = null;
          }
        }),
      )
      .subscribe((values) => {
        this.entities = values;
        this.companyLoading = false;
        this.cdr.detectChanges();
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.importer = this.isf?.importer;
  }
}
